/*
* User Management Page (please do not remove this comment)
* This page is responsible for managing user accounts, including creating, updating, and deleting users.
* It provides a user-friendly interface for administrators to manage user accounts effectively.
* User fields include email, password, role, group, update date, and creation date.
* The page also includes a list of all users with options to edit or delete each user.
* to get the list of users, we need to call the API endpoint GET /api/v2/users/list
* to update a user, we need to call the API endpoint PATCH /api/v2/users/:email
* to create a user, we need to call the API endpoint POST /api/v2/users/signup
* to delete a user, we need to call the API endpoint DELETE /api/v2/users/:email
* The page should also include a form to create a new user, with fields for email, password, role, and group.
* The form should validate the input and display error messages if the input is invalid.
* The page should also include a confirmation dialog before deleting a user.
* The page should also include a search bar to filter the list of users by email or role.
* The page should also include pagination to limit the number of users displayed at once.
*
* history:
* move user list to the top of the page
* add search bar to filter the list of users by email or role
* add pagination default to 25 per page, width 100%
*
* add a buttom to create a new user in the top right corner of user list
* and change create user form to dialog, when click create user button, open the dialog
* and click click edit user button, open the dialog with user data
*
* move actions before the created and updated date
*/

import React, { useState, useEffect } from 'react';
import { 
  Container, Typography, Box, Paper, TextField, Button, 
  FormControl, InputLabel, Select, MenuItem, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  IconButton, InputAdornment, Alert, Grid, Snackbar, CircularProgress,
  Tooltip, Chip
} from '@mui/material';
import { Search as SearchIcon, Edit as EditIcon, Delete as DeleteIcon, Visibility as VisibilityIcon, 
  VisibilityOff as VisibilityOffIcon, Refresh as RefreshIcon, PersonAdd as PersonAddIcon } from '@mui/icons-material';
import { useAuthContext } from '../hooks/useAuthContext';

function UserManagement() {
  // State variables
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const { user } = useAuthContext();
  
  // Form state
  const [formMode, setFormMode] = useState('create'); // 'create' or 'edit'
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    role: 'user',
    group: 'unspecified'
  });
  const [formErrors, setFormErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  
  // Dialog states
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25); // Default to 25 as requested
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  
  // Fetch users on component mount
  useEffect(() => {
    fetchUsers();
  }, [user?.token]); // Re-fetch when token changes
  
  const fetchUsers = async () => {
    if (!user?.token) return;
    
    try {
      setLoading(true);
      const response = await fetch('/api/v2/users/list', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized. Please log in again.');
        }
        throw new Error(`API error: ${response.status}`);
      }
      
      const data = await response.json();
      setUsers(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(`Failed to load users: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Open create user dialog
  const handleCreateUserClick = () => {
    setFormMode('create');
    resetForm();
    setFormDialogOpen(true);
  };
  
  // Form validation
  const validateForm = () => {
    const errors = {};
    
    // Email validation
    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }
    
    // Password validation (only for create mode or if password field is filled in edit mode)
    if (formMode === 'create' && !formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password && formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters long';
    }
    
    // Role validation
    if (!formData.role) {
      errors.role = 'Role is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      let response;
      
      if (formMode === 'create') {
        // Create new user
        response = await fetch('/api/v2/users/signup', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${user.token}`,
          },
          body: JSON.stringify(formData),
        });
      } else {
        // Update existing user
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password; // Don't send empty password
        }
        
        response = await fetch(`/api/v2/users/${formData.email}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${user.token}`,
          },
          body: JSON.stringify(updateData),
        });
      }
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `API error: ${response.status}`);
      }
      
      // Success
      setSuccess(formMode === 'create' ? 'User created successfully!' : 'User updated successfully!');
      resetForm();
      setFormDialogOpen(false); // Close dialog after successful submission
      fetchUsers(); // Refresh user list
    } catch (err) {
      console.error('Error submitting form:', err);
      setError(`Failed to ${formMode} user: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    // Clear error for this field when user types
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: undefined,
      });
    }
  };
  
  // Reset form
  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      role: 'user',
      group: 'unspecified'
    });
    setFormErrors({});
  };
  
  // Edit user
  const handleEditUser = (userToEdit) => {
    setFormData({
      email: userToEdit.email,
      password: '', // Don't show password
      role: userToEdit.role || 'user',
      group: userToEdit.group || 'unspecified'
    });
    setFormMode('edit');
    setFormDialogOpen(true);
  };
  
  // Delete user
  const handleDeleteClick = (userToEdit) => {
    // Prevent self-deletion
    if (userToEdit.email === user.email) {
      setError("You cannot delete your own account");
      return;
    }
    
    setUserToDelete(userToEdit);
    setDeleteDialogOpen(true);
  };
  
  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      setLoading(true);
      const response = await fetch(`/api/v2/users/${userToDelete.email}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `API error: ${response.status}`);
      }
      
      setSuccess('User deleted successfully!');
      fetchUsers(); // Refresh user list
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(`Failed to delete user: ${err.message}`);
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };
  
  // Search functionality
  const filteredUsers = users.filter(user => 
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.role?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.group?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };
  
  // Close snackbar
  const handleCloseSnackbar = () => {
    setSuccess(null);
    setError(null);
  };
  
  // Role color mapper
  const getRoleColor = (role) => {
    switch(role) {
      case 'admin': return 'error';
      case 'operator': return 'warning';
      case 'user': return 'primary';
      default: return 'default';
    }
  };

  // Refresh button handler
  const handleRefresh = () => {
    fetchUsers();
  };
  
  // Close form dialog
  const handleFormDialogClose = () => {
    setFormDialogOpen(false);
    resetForm();
  };
  
  return (
    <Container maxWidth="xl" sx={{ mb: 5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', my: 3 }}>
        <Typography variant="h4" component="h1">
          User Management
        </Typography>
        <Button 
          startIcon={<RefreshIcon />} 
          onClick={handleRefresh}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>
      
      {/* Error and Success Alerts */}
      <Snackbar 
        open={Boolean(success)} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
      
      <Snackbar 
        open={Boolean(error)} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
      
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">User List</Typography>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              size="small"
              placeholder="Search users..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            
            {/* Create user button in top right corner as requested */}
            <Button
              variant="contained"
              color="primary"
              startIcon={<PersonAddIcon />}
              onClick={handleCreateUserClick}
            >
              Add User
            </Button>
          </Box>
        </Box>
        
        {loading && !users.length ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Email</strong></TableCell>
                    <TableCell><strong>Role</strong></TableCell>
                    <TableCell><strong>Group</strong></TableCell>
                    <TableCell align="center"><strong>Actions</strong></TableCell>
                    <TableCell><strong>Created</strong></TableCell>
                    <TableCell><strong>Updated</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((userItem) => (
                    <TableRow key={userItem._id || userItem.email}>
                      <TableCell>{userItem.email}</TableCell>
                      <TableCell>
                        <Chip 
                          label={userItem.role || 'user'} 
                          color={getRoleColor(userItem.role)} 
                          size="small" 
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{userItem.group || 'unspecified'}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Edit user">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleEditUser(userItem)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={userItem.email === user.email ? "Cannot delete your own account" : "Delete user"}>
                          <span>
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleDeleteClick(userItem)}
                              disabled={userItem.email === user.email}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{formatDate(userItem.createdAt)}</TableCell>
                      <TableCell>{formatDate(userItem.updatedAt)}</TableCell>
                    </TableRow>
                  ))}
                  
                  {filteredUsers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        {users.length === 0 ? 'No users found' : 'No matching users found'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              component="div"
              count={filteredUsers.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>
      
      {/* User Form Dialog - replaces the form at bottom of page */}
      <Dialog 
        open={formDialogOpen} 
        onClose={handleFormDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {formMode === 'create' ? 'Create New User' : 'Edit User'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" noValidate sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  error={Boolean(formErrors.email)}
                  helperText={formErrors.email}
                  disabled={formMode === 'edit'} // Email cannot be changed in edit mode
                />
                
                <TextField
                  margin="normal"
                  required={formMode === 'create'}
                  fullWidth
                  name="password"
                  label={formMode === 'create' ? 'Password' : 'Password (leave blank to keep unchanged)'}
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="new-password"
                  value={formData.password}
                  onChange={handleInputChange}
                  error={Boolean(formErrors.password)}
                  helperText={formErrors.password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="role-label">Role</InputLabel>
                  <Select
                    labelId="role-label"
                    id="role"
                    name="role"
                    value={formData.role}
                    label="Role"
                    onChange={handleInputChange}
                    error={Boolean(formErrors.role)}
                  >
                    <MenuItem value="user">User</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="operator">Operator</MenuItem>
                  </Select>
                  {formErrors.role && (
                    <Typography color="error" variant="caption">
                      {formErrors.role}
                    </Typography>
                  )}
                </FormControl>
                
                <TextField
                  margin="normal"
                  fullWidth
                  id="group"
                  label="Group"
                  name="group"
                  value={formData.group}
                  onChange={handleInputChange}
                  autoComplete="off"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFormDialogClose}>Cancel</Button>
          <Button 
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : (formMode === 'create' ? 'Create User' : 'Update User')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete user <strong>{userToDelete?.email}</strong>? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDeleteUser} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default UserManagement;